import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CandidateHeader from './components/CandidateHeader';
import TabNavigation from './components/TabNavigation';
import ResumeViewer from './components/ResumeViewer';
import DocumentsTab from './components/DocumentsTab';
import InterviewFeedback from './components/InterviewFeedback';
import CommunicationHistory from './components/CommunicationHistory';
import ApplicationProgress from './components/ApplicationProgress';
import CoverLetterTab from './components/CoverLetterTab';
import ParsedResumeViewer from './components/ParsedResumeViewer';
import ResumeUploadModal from './components/ResumeUploadModal';
import Header from '../../components/ui/Header';
import { candidateService } from '../../services/candidateService';
import { useAuth } from '../../contexts/AuthContext';

const CandidateProfile = () => {
  const { candidateId } = useParams();
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('resume');
  const [candidate, setCandidate] = useState(null);
  const [application, setApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Enhanced candidateId validation and fallback handling
  const getEffectiveCandidateId = () => {
    if (candidateId && candidateId !== 'undefined' && candidateId !== 'null') {
      return candidateId;
    }
    
    // Fallback to a default ID for demo purposes
    return '1';
  };

  const effectiveCandidateId = getEffectiveCandidateId();

  const tabs = [
    { id: 'resume', label: 'Resume', icon: '📄' },
    { id: 'ai-analysis', label: 'AI Analysis', icon: '🤖' },
    { id: 'documents', label: 'Documents', icon: '📁' },
    { id: 'interviews', label: 'Interview Notes', icon: '💼' },
    { id: 'communication', label: 'Communication', icon: '💬' },
    { id: 'progress', label: 'Progress', icon: '📊' },
    { id: 'cover-letter', label: 'Cover Letter', icon: '✉️' }
  ];

  // Load candidate data from Supabase
  useEffect(() => {
    loadCandidateData();
  }, [candidateId, userProfile?.org_id]);

  const loadCandidateData = async () => {
    if (!effectiveCandidateId) {
      setError('No candidate ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await candidateService?.getCandidateWithApplications(effectiveCandidateId);
      
      if (fetchError) {
        // Show mock data for preview if real data fails
        setCandidate({
          id: effectiveCandidateId,
          full_name: 'Sample Candidate',
          email: 'sample@example.com',
          phone: '+1 (555) 123-4567',
          location: 'San Francisco, CA',
          resume_text: 'Sample resume content',
          resume_url: null,
          source: 'Direct application',
          created_at: new Date()?.toISOString()
        });
        setApplications([]);
        setApplication({
          id: '1',
          stage: 'applied',
          jobs: {
            title: 'Software Engineer',
            department: 'Engineering'
          }
        });
        setError('Using demo data - connect to Supabase for real candidate information');
        setLoading(false);
        return;
      }

      setCandidate(data);
      setApplications(data?.applications || []);
      
      // Set the primary application (first one or most recent)
      const primaryApplication = data?.applications?.[0] || null;
      setApplication(primaryApplication);
      
      if (primaryApplication?.jobs?.id) {
        setSelectedJobId(primaryApplication?.jobs?.id);
      }

    } catch (err) {
      console.error('Error loading candidate:', err);
      setError('Failed to load candidate data');
      // Fallback to mock data
      setCandidate({
        id: effectiveCandidateId,
        full_name: 'Sample Candidate',
        email: 'sample@example.com',
        phone: '+1 (555) 123-4567',
        location: 'San Francisco, CA',
        resume_text: 'Sample resume content...',
        source: 'Direct application',
        created_at: new Date()?.toISOString()
      });
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    // Refresh data when upload succeeds
    setShowUploadModal(false);
    loadCandidateData();
  };

  const handleCandidateUpdate = (updatedApplication) => {
    // Update the application state when candidate is hired
    if (updatedApplication) {
      setApplication(updatedApplication);
      
      // Update applications array
      const updatedApplications = applications?.map(app => 
        app?.id === updatedApplication?.id ? updatedApplication : app
      );
      setApplications(updatedApplications);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'resume':
        return <ResumeViewer 
          candidate={candidate} 
          resume={candidate?.resume_text || candidate?.resume || ''} 
        />;
      case 'ai-analysis':
        return (
          <ParsedResumeViewer 
            candidateId={candidateId}
            selectedJobId={selectedJobId}
          />
        );
      case 'documents':
        return <DocumentsTab 
          candidateId={candidateId} 
          documents={candidate?.documents || []} 
        />;
      case 'interviews':
        return <InterviewFeedback 
          candidateId={candidateId} 
          interviews={candidate?.interviews || []}
          onAddFeedback={() => {}}
        />;
      case 'communication':
        return <CommunicationHistory 
          candidateId={candidateId} 
          communications={candidate?.communications || []}
          onSendMessage={() => {}}
        />;
      case 'progress':
        return <ApplicationProgress 
          applications={applications}
          stages={['new', 'applied', 'screening', 'interview', 'final_review', 'offer', 'hired']}
          currentStage={application?.stage || 'applied'}
          onStageClick={() => {}}
        />;
      case 'cover-letter':
        return <CoverLetterTab 
          candidate={candidate} 
          coverLetter={candidate?.cover_letter || candidate?.coverLetter || ''}
        />;
      default:
        return <ResumeViewer 
          candidate={candidate} 
          resume={candidate?.resume_text || candidate?.resume || ''} 
        />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading candidate data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center">
            <span className="text-yellow-600 mr-2">⚠️</span>
            <p className="text-yellow-800">{error}</p>
          </div>
        </div>
      )}

      {/* Header with Upload Button */}
      <div className="flex items-center justify-between mb-8">
        <CandidateHeader 
          candidate={candidate}
          application={application}
          applications={applications}
          onScheduleInterview={() => {}}
          onSendMessage={() => {}}
          onMoveStage={() => {}}
          onCandidateUpdate={handleCandidateUpdate}
        />
        <button
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
        >
          <span>📤</span>
          <span>Upload Resume</span>
        </button>
      </div>

      {/* Job Selection for AI Analysis */}
      {activeTab === 'ai-analysis' && applications?.length > 0 && (
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Compare with Job Position (Optional)
          </label>
          <select
            value={selectedJobId || ''}
            onChange={(e) => setSelectedJobId(e?.target?.value || null)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a job position...</option>
            {applications?.map((app) => (
              <option key={app?.jobs?.id} value={app?.jobs?.id}>
                {app?.jobs?.title} - {app?.jobs?.department}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tab Navigation */}
      <TabNavigation 
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Tab Content */}
      <div className="mt-8">
        {renderTabContent()}
      </div>

      {/* Upload Modal */}
      <ResumeUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        candidateId={effectiveCandidateId}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default CandidateProfile;